// pages/MenuPage.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout, Button, Typography, Divider, Row, Col, Card, Avatar, Badge, Modal, Dropdown, message, Spin } from 'antd';
import type { MenuProps } from 'antd';
import {
  CameraOutlined,
  HistoryOutlined,
  TeamOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  UserAddOutlined,
  BellOutlined,
  InboxOutlined,
  ContainerOutlined,
  CheckCircleOutlined,
  MenuOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logWarning } from '../utils/errorLogger';
import { getRoutePath } from '../config';
import { isInFlutter } from '../utils/flutterDetection';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { followAPI } from '../services/follow-api';
import { businessCardAPI } from '../services/api';
import FollowListModal, { FollowItem } from '../components/FollowListModal';
import SettingsModal from '../components/SettingsModal';
import MaintenanceMessageBanner from '../components/MaintenanceMessageBanner';
import TemporaryCustomerListModal from '../components/TemporaryCustomerListModal';
import { UPLOAD_IMAGES_KEY } from './ImageUploadPage';
import { refreshCapturedEmergencyMode } from '../utils/chatGptOcr';
import { useContactUsers } from '../hooks/useContactUsers';
import {
  SEARCH_FORM_VALUES_KEY,
  SEARCH_PARAMS_KEY,
  SELECTED_CONTACT_USERS_KEY,
  SCROLL_POSITION_KEY,
  LOADED_ITEMS_COUNT_KEY,
} from '../constants/storageKeys';
import { hasAuth, AUTH } from '../utils/auth';

declare global {
  interface Window {
    FlutterCamera?: {
      postMessage: (message: string) => void;
    };
  }
}

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  description: string;
  onClick: () => void;
  color: string;
  /** 表示に必要なロール権限ID */
  auth?: string;
}

const MenuPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user, authorityResolved } = useAuth();

  // 「画像をアップロードして登録する」用のファイル選択（Web=ファイル選択 / アプリ=カメラロール）
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const readFileAsDataURL = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  const handleUploadFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    // 同じファイルを再選択できるよう input をリセット
    e.target.value = '';
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    try {
      const items = await Promise.all(
        imageFiles.map(async (f) => ({
          original: await readFileAsDataURL(f),
          extracted: null,
        }))
      );
      await indexedDBStorage.setItem(UPLOAD_IMAGES_KEY, JSON.stringify(items));
      navigate(getRoutePath('/image-upload'));
    } catch (err) {
      console.error('画像読み込みエラー:', err);
      message.error('画像の読み込みに失敗しました');
    }
  };

  // 通知モーダルの表示状態
  const [notifMenuVisible, setNotifMenuVisible] = useState<boolean>(false);
  // 仮登録顧客一覧モーダルの表示状態（フェーズ1: UIのみ）
  const [tempCustomerListVisible, setTempCustomerListVisible] = useState<boolean>(false);
  // 設定モーダルの表示状態
  const [settingsVisible, setSettingsVisible] = useState<boolean>(false);
  // 未確認フォロー一覧モーダルの表示状態
  const [unconfirmedFollowVisible, setUnconfirmedFollowVisible] = useState<boolean>(false);
  // 仮登録フォロー一覧モーダルの表示状態
  const [temporaryFollowVisible, setTemporaryFollowVisible] = useState<boolean>(false);
  // 予定ToDoフォロー一覧モーダルの表示状態
  const [todoFollowVisible, setTodoFollowVisible] = useState<boolean>(false);

  // 未読フォロー件数（バッジ表示）
  const [followUnreadCount, setFollowUnreadCount] = useState<number>(0);
  // 仮登録フォロー件数（バッジ表示）
  const [followTempCount, setFollowTempCount] = useState<number>(0);
  // 予定ToDo件数（バッジ表示）
  const [followTodoCount, setFollowTodoCount] = useState<number>(0);
  // 仮登録顧客件数（バッジ表示）
  const [tempCustomerCount, setTempCustomerCount] = useState<number>(0);
  // 未確認フォロー一覧
  const [unconfirmedFollowItems, setUnconfirmedFollowItems] = useState<FollowItem[]>([]);
  // 仮登録フォロー一覧
  const [temporaryFollowItems, setTemporaryFollowItems] = useState<FollowItem[]>([]);
  // 予定ToDoフォロー一覧
  const [todoFollowItems, setTodoFollowItems] = useState<FollowItem[]>([]);
  // 担当者一覧
  const { contactUsers: notifContactUsers } = useContactUsers();

  // Flutterアプリ内で実行されているかチェック
  const isFlutterApp = isInFlutter();

  // ユーザー情報から表示名を取得
  const displayName = user?.name || user?.username || 'ユーザー';
  const departmentName = user?.deptname || '';
  const userImageUrl = user?.image || '';

  // 顧客の検索権限（「名刺を検索する」メニュー・通知ベルマークの表示制御に使用）
  const canSearchCustomer = hasAuth(user, AUTH.SC_CUSTOMER_KARTE);
  // 顧客の登録/編集権限（「名刺を撮影する」「顧客を追加する」カードの表示制御に使用）
  const canRegisterCustomer = hasAuth(user, AUTH.SC_CUSTOMER_EDIT);
  // メニュー画面で何かしら操作可能か（false の場合は権限なしメッセージを表示）
  const hasAnyMenuAuth = canSearchCustomer || canRegisterCustomer;

  // authoritylist を実際に取得できたか。[] は「取得済みで権限が空」、null/undefined は
  // 「そもそも未取得（getLoginUserInfo が届かなかった）」で意味が異なる。
  // 前者だけが本当の無権限で、後者は権限がサーバ上にあっても取得失敗した誤検知層。
  const authorityKnown = Array.isArray(user?.authoritylist);

  // 権限確定後にメニュー権限が無い状態を、実際にバナー表示した瞬間に1回だけ記録する
  // （トークンは正常でエラーを出さないため、エラー同梱では捕捉できないケースを拾う）。
  // authorityKnown で「真の無権限」と「取得失敗」を分離し、診断ログを取り違えないようにする。
  // 認証/権限スナップショット（userno・authoritylistの形/件数等）は errorLogger 側で自動同梱。
  const authBannerLoggedRef = useRef(false);
  useEffect(() => {
    if (authBannerLoggedRef.current) return;
    if (!authorityResolved || hasAnyMenuAuth) return;
    authBannerLoggedRef.current = true;
    if (authorityKnown) {
      // authoritylist は取得できたが操作権限が無い＝再ログインしても消えない真の無権限層。
      logWarning('diag.menuAuthBanner', '権限なしバナー表示（権限確定後もメニュー権限なし）', {
        note: 'authDiag（同梱）の authoritylistType/Length/Sample・getLoginUserInfoStatus を参照',
      });
    } else {
      // authoritylist を取得できないまま確定（getLoginUserInfo 未達）。権限はサーバ上に
      // あるのに未取得＝従来 menuAuthBanner に誤って混入していた層。区別して記録する。
      logWarning('diag.menuAuthUnresolved', '権限リスト未取得のまま権限なし扱い（getLoginUserInfo 未達）', {
        note: 'authDiag（同梱）の getLoginUserInfoStatus・authoritylistType を参照。真の無権限ではなく取得失敗の疑い',
      });
    }
  }, [authorityResolved, hasAnyMenuAuth, authorityKnown]);

  /** 未確認フォロー一覧を取得してセットする（件数バッジも同時更新） */
  const loadUnconfirmedFollows = useCallback(async () => {
    const items = await followAPI.getUnconfirmedFollows();
    setUnconfirmedFollowItems(items);
    setFollowUnreadCount(items.length);
  }, []);

  /** 仮登録フォロー一覧を取得してセットする（件数バッジも同時更新） */
  const loadTemporaryFollows = useCallback(async () => {
    const contactusernoList = user?.userno ? [user.userno] : [];
    const items = await followAPI.getTemporaryFollows(contactusernoList);
    setTemporaryFollowItems(items);
    setFollowTempCount(items.length);
  }, [user]);

  /** 予定ToDoフォロー一覧を取得してセットする（件数バッジも同時更新） */
  const loadTodoFollows = useCallback(async () => {
    const contactusernoList = user?.userno ? [user.userno] : [];
    const items = await followAPI.getTodoFollows(contactusernoList);
    setTodoFollowItems(items);
    setFollowTodoCount(items.length);
  }, [user]);

  /** 仮登録顧客件数を取得してバッジを更新する（TemporaryCustomerListModal と同じ絞り込み条件で取得） */
  const loadTempCustomerCount = useCallback(async () => {
    const res = await businessCardAPI.countTemporaryCustomers({
      isAllUser: false,
      searchCreatefunction: 9,
    });
    if (res?.statuscode === 200 && typeof res.customertemplistsize === 'number') {
      setTempCustomerCount(res.customertemplistsize);
    }
  }, []);

  /** マウント時：バッジ件数・各フォロー一覧を一括取得（通知ベル非表示＝カルテ閲覧権限なしの場合は取得しない） */
  useEffect(() => {
    if (!canSearchCustomer) return;

    // ① 件数系（軽量・2本）：最優先で先に投げる
    followAPI.getTodoCount().then(({ followUnreadCount: unread, followTempCount: temp, followTodoCount: todo }) => {
      setFollowUnreadCount(unread);
      setFollowTempCount(temp);
      setFollowTodoCount(todo);
    });
    loadTempCustomerCount();

    // ② 詳細一覧（重い・3本）：①の直後に、裏側で並行して投げる（画面表示はブロックしない）
    loadUnconfirmedFollows();
    loadTemporaryFollows();
    loadTodoFollows();
  }, [user?.userno]); // eslint-disable-line react-hooks/exhaustive-deps

  // 緊急モード（ComSystemMapper の UseChatGPT が true）。true の間は画像アップロード登録を不可にする。
  const [emergencyMode, setEmergencyMode] = useState<boolean>(false);

  /** メニュー復帰時に緊急モードを取り直して保持し直す（次の撮影フローの基準にする）。 */
  useEffect(() => {
    (async () => {
      const useChatGpt = await refreshCapturedEmergencyMode();
      console.log('UseChatGPT (refreshed at menu):', useChatGpt);
      setEmergencyMode(useChatGpt);
    })();
  }, []);

  /** 本登録モードの編集フローから戻ってきた場合に、仮登録顧客一覧モーダルを自動再オープン */
  useEffect(() => {
    (async () => {
      const flag = await indexedDBStorage.getItem('reopenTempCustomerListAfterReturn');
      if (flag === 'true') {
        await indexedDBStorage.removeItem('reopenTempCustomerListAfterReturn');
        setTempCustomerListVisible(true);
      }
    })();
  }, []);

  /** 「未読にする」確定後：一覧から該当アイテムを除き、バッジも減らす */
  const handleMarkUnread = (item: FollowItem) => {
    setUnconfirmedFollowItems(prev => prev.filter(f => f.followno !== item.followno));
    setFollowUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleLogout = (): void => {
    logout();
    navigate(getRoutePath('/login'));
  };

  const menuItems: MenuItem[] = [
    {
      title: '名刺を検索する',
      icon: <TeamOutlined style={{ fontSize: 32 }} />,
      description: '登録済みの名刺を検索します',
      onClick: () => {
        // 名刺一覧画面に移動する前に検索条件のローカルストレージを破棄
        localStorage.removeItem(SEARCH_FORM_VALUES_KEY);
        localStorage.removeItem(SEARCH_PARAMS_KEY);
        localStorage.removeItem(SELECTED_CONTACT_USERS_KEY);
        localStorage.removeItem(SCROLL_POSITION_KEY);
        localStorage.removeItem(LOADED_ITEMS_COUNT_KEY);

        navigate(getRoutePath('/business-card-list'));
      },
      color: '#fa8c16',
      auth: AUTH.SC_CUSTOMER_KARTE
    },
    {
      title: '名刺を撮影する',
      icon: <CameraOutlined style={{ fontSize: 32 }} />,
      description: '新しい名刺を撮影して登録します',
      onClick: () => navigate(getRoutePath('/camera')),
      color: '#0693e3',
      auth: AUTH.SC_CUSTOMER_EDIT
    },
    /*
    {
      title: '名刺履歴',
      icon: <HistoryOutlined style={{ fontSize: 32 }} />,
      description: '登録済みの名刺履歴を確認します',
      onClick: () => {},
      color: '#52c41a'
    },
    {
      title: 'コンタクト管理',
      icon: <TeamOutlined style={{ fontSize: 32 }} />,
      description: '登録済みの連絡先を管理します',
      onClick: () => {},
      color: '#fa8c16'
    },
    {
      title: '設定',
      icon: <SettingOutlined style={{ fontSize: 32 }} />,
      description: 'アプリの設定を変更します',
      onClick: () => {},
      color: '#722ed1'
    }
      */
  ];

  // ロール権限で表示するメニューを絞り込む
  const visibleMenuItems = menuItems.filter(item => !item.auth || hasAuth(user, item.auth));

  return (
    <Layout style={{ minHeight: '100vh', background: isFlutterApp ? '#f0f2f5' : '#fff' }}>
      <Header style={{
        background: '#fff',
        padding: '0 16px',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 'auto',
        minHeight: '64px',
        width: '100%'
      }}>
        <div style={{
          flex: '1 1 auto',
          marginRight: '8px',
          minWidth: '150px',
          display: isFlutterApp ? 'none' : 'block'
        }}>
          <img
            src="./logo.png"
            alt="UP-TONE"
            style={{
              height: '24px',
              width: 'auto',
              objectFit: 'contain',
              margin: '12px 0'
            }}
          />
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isFlutterApp ? 'space-between' : 'flex-end',
          flex: isFlutterApp ? '1 1 100%' : '0 0 auto',
          width: isFlutterApp ? '100%' : 'auto'
        }}>
          {/* ユーザー情報を表示するエリアを強化 */}
          <div style={{ display: 'flex', alignItems: 'center', marginRight: isFlutterApp ? 0 : 12 }}>
            {/* ユーザーアイコン - 画像URLがあれば表示、なければデフォルトアイコン */}
            {userImageUrl ? (
              <Avatar src={userImageUrl} size="small" style={{ marginRight: 8 }} />
            ) : (
              <Avatar icon={<UserOutlined />} size="small" style={{ marginRight: 8 }} />
            )}

            {/* ユーザー情報テキスト */}
            <div style={{ lineHeight: 1.2 }}>
              <Text style={{ fontSize: '14px', display: 'block' }}>{displayName}</Text>
              {departmentName && (
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>{departmentName}</Text>
              )}
            </div>
          </div>

          {/* ハンバーガーメニュー（設定・ログアウトをまとめる） */}
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'settings',
                  icon: <SettingOutlined />,
                  label: '設定',
                  onClick: () => setSettingsVisible(true),
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: 'ログアウト',
                  onClick: handleLogout,
                },
              ] as MenuProps['items'],
            }}
          >
            <Button
              type="text"
              icon={<MenuOutlined />}
              size="small"
              aria-label="メニュー"
              style={{ marginLeft: 'auto' }}
            />
          </Dropdown>
        </div>
      </Header>

      {/* メンテナンスメッセージバナー */}
      <MaintenanceMessageBanner />

      {/* 新しいアイコンルックデザイン（コメントアウトして保持）
      <Content style={{
        padding: '32px 20px',
        background: '#f5f5f5',
        minHeight: 'calc(100vh - 128px)'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '24px',
          maxWidth: '600px',
          margin: '0 auto',
          justifyItems: 'center'
        }}>
          {menuItems.map((item, index) => (
            <div
              key={index}
              onClick={item.onClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px 16px',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '120px',
                maxWidth: '140px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{
                color: item.color,
                marginBottom: '12px',
                fontSize: '40px',
                lineHeight: 1
              }}>
                {item.icon}
              </div>
              <Text style={{
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'center',
                color: '#333',
                lineHeight: '1.3'
              }}>
                {item.title}
              </Text>
            </div>
          ))}
        </div>
      </Content>
      */}

      {/* 元のデザインに戻す */}
      <Content style={{
        // アプリ（Flutter）のフッターで最下部が隠れないよう、下に広めのマージンを確保する
        padding: isFlutterApp ? '24px 24px 120px' : '24px',
        background: '#f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flexGrow: 1,
        minHeight: isFlutterApp ? 'calc(100vh - 64px)' : 'calc(100vh - 128px)',
        position: 'relative',
      }}>

        {hasAnyMenuAuth && <Divider />}

        {/*
         * TODO: 通知機能 仮実装
         * - hasUnread / unconfirmedCount は固定値。本実装時はAPIから未読フラグ・未確認件数を取得すること。
         * - 「未確認フォロー」ボタンのonClickも未実装。本実装時は未確認フォロー一覧画面への遷移処理を追加すること。
         * - 「仮登録フォロー」は将来実装予定のため現在は非アクティブ。本実装時にonClickと有効化処理を追加すること。
         */}
        {/* 通知ベルボタン：Contentエリア右上（カルテ閲覧権限がある場合のみ表示） */}
        {canSearchCustomer && (
          <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
            <Badge dot={followUnreadCount > 0 || followTodoCount > 0} color="#ff4d4f" offset={[-4, 4]}>
              <button
                onClick={() => setNotifMenuVisible(true)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  color: '#595959',
                  fontSize: 18,
                }}
              >
                <BellOutlined />
              </button>
            </Badge>
          </div>
        )}

        {/* 通知メニューモーダル */}
        <Modal
          open={notifMenuVisible}
          onCancel={() => setNotifMenuVisible(false)}
          footer={null}
          title="通知"
          centered
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center', padding: '16px 8px' }}>
            {/* 予定ToDoフォローボタン */}
            <Badge count={followTodoCount} color="#ff4d4f" offset={[-10, 8]}>
              <button
                onClick={() => {
                  setNotifMenuVisible(false);
                  setTodoFollowVisible(true);
                }}
                style={{
                  width: 84,
                  height: 92,
                  borderRadius: 12,
                  backgroundColor: '#fff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  padding: 0,
                  color: '#595959',
                }}
              >
                <CheckCircleOutlined style={{ fontSize: 26, color: '#52c41a' }} />
                <span style={{ fontSize: 11, lineHeight: 1.3, textAlign: 'center' }}>本日の<br />ToDo</span>
              </button>
            </Badge>

            {/* 未確認フォローボタン */}
            <Badge count={followUnreadCount} color="#ff4d4f" offset={[-10, 8]}>
              <button
                onClick={() => {
                  setNotifMenuVisible(false);
                  setUnconfirmedFollowVisible(true);
                }}
                style={{
                  width: 84,
                  height: 92,
                  borderRadius: 12,
                  backgroundColor: '#fff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  padding: 0,
                  color: '#595959',
                }}
              >
                <InboxOutlined style={{ fontSize: 26, color: '#0693e3' }} />
                <span style={{ fontSize: 11, lineHeight: 1.3, textAlign: 'center' }}>未確認<br />フォロー</span>
              </button>
            </Badge>

            {/* 仮登録フォローボタン */}
            <Badge count={followTempCount} color="#ff4d4f" offset={[-10, 8]}>
              <button
                onClick={() => {
                  setNotifMenuVisible(false);
                  setTemporaryFollowVisible(true);
                }}
                style={{
                  width: 84,
                  height: 92,
                  borderRadius: 12,
                  backgroundColor: '#fff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  padding: 0,
                  color: '#595959',
                }}
              >
                <ContainerOutlined style={{ fontSize: 26, color: '#fa8c16' }} />
                <span style={{ fontSize: 11, lineHeight: 1.3, textAlign: 'center' }}>仮登録<br />フォロー</span>
              </button>
            </Badge>

            {/* 仮登録顧客ボタン（顧客登録/編集権限がある場合のみ表示） */}
            {canRegisterCustomer && (
            <Badge count={tempCustomerCount} color="#ff4d4f" offset={[-10, 8]}>
              <button
                onClick={() => {
                  setNotifMenuVisible(false);
                  setTempCustomerListVisible(true);
                }}
                style={{
                  width: 84,
                  height: 92,
                  borderRadius: 12,
                  backgroundColor: '#fff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  padding: 0,
                  color: '#595959',
                }}
              >
                <UserAddOutlined style={{ fontSize: 26, color: '#13c2c2' }} />
                <span style={{ fontSize: 11, lineHeight: 1.3, textAlign: 'center' }}>仮登録<br />顧客</span>
              </button>
            </Badge>
            )}

          </div>
        </Modal>

        {/* 予定ToDoフォロー一覧・詳細コンポーネント */}
        <FollowListModal
          open={todoFollowVisible}
          onClose={() => setTodoFollowVisible(false)}
          title="本日のToDo"
          items={todoFollowItems}
          contactUsers={notifContactUsers}
          onRefresh={loadTodoFollows}
          features={{ showMarkUnread: false }}
        />

        {/* 未確認フォロー一覧・詳細コンポーネント */}
        <FollowListModal
          open={unconfirmedFollowVisible}
          onClose={() => { setUnconfirmedFollowVisible(false); loadUnconfirmedFollows(); }}
          title="未確認フォロー"
          items={unconfirmedFollowItems}
          contactUsers={notifContactUsers}
          onMarkUnread={handleMarkUnread}
          onRefresh={loadUnconfirmedFollows}
        />

        {/* 仮登録フォロー一覧・詳細コンポーネント */}
        <FollowListModal
          open={temporaryFollowVisible}
          onClose={() => setTemporaryFollowVisible(false)}
          title="仮登録フォロー"
          items={temporaryFollowItems}
          contactUsers={notifContactUsers}
          onRefresh={loadTemporaryFollows}
          features={{ showMarkUnread: false }}
        />

        {/* 設定モーダル */}
        <SettingsModal
          open={settingsVisible}
          onClose={() => setSettingsVisible(false)}
        />

        {/* 仮登録顧客一覧モーダル */}
        <TemporaryCustomerListModal
          open={tempCustomerListVisible}
          onClose={() => setTempCustomerListVisible(false)}
          onCountChange={setTempCustomerCount}
        />

        {hasAnyMenuAuth ? (
        <>
        <Row gutter={[16, 16]} justify="center" align="middle">
          {visibleMenuItems.map((item, index) => (
            <Col key={index} xs={24} sm={12} md={12} lg={8} xl={6}>
              <Card
                hoverable
                onClick={item.onClick}
                style={{
                  textAlign: 'center',
                  height: '100%',
                  borderTop: `2px solid ${item.color}`,
                  marginBottom: '8px',
                  maxWidth: '400px',
                  margin: '0 auto'
                }}
              >
                <div style={{ color: item.color, marginBottom: 16 }}>
                  {item.icon}
                </div>
                <Title level={5} style={{ marginBottom: 8, fontSize: '16px' }}>{item.title}</Title>
                <Text type="secondary" style={{ fontSize: '14px' }}>{item.description}</Text>
              </Card>
            </Col>
          ))}
        </Row>

        {/* メニューカードと顧客追加カードの両方がある場合のみ中間 Divider を表示 */}
        {visibleMenuItems.length > 0 && canRegisterCustomer && <Divider />}

        {/* サブ機能エリア */}
        {canRegisterCustomer && (
        <Row justify="center">
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            {/* 画像アップロード用の隠しファイル入力（Web=ファイル選択 / アプリ=カメラロール、複数選択可） */}
            <input
              ref={uploadFileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleUploadFilesSelected}
              style={{ display: 'none' }}
            />
            {/* 画像をアップロードして名刺を登録する（従来の「撮影しないで〜」と同等のデザイン強度） */}
            <Card
              hoverable={!emergencyMode}
              onClick={() => { if (!emergencyMode) uploadFileInputRef.current?.click(); }}
              style={{
                borderTop: '2px solid #d9d9d9',
                maxWidth: '400px',
                margin: '0 auto',
                background: '#f7f7f7',
                opacity: emergencyMode ? 0.5 : 1,
                cursor: emergencyMode ? 'not-allowed' : 'pointer',
              }}
              styles={{ body: { padding: '8px 16px' } }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PictureOutlined style={{ fontSize: 22, color: '#8c8c8c', lineHeight: 1, display: 'flex' }} />
                  <Title level={5} style={{ margin: 0, fontSize: '15px', lineHeight: '22px' }}>画像をアップロードして登録する</Title>
                </div>
                {emergencyMode ? (
                  <Text style={{ fontSize: '13px', color: '#cf1322' }}>現在緊急モードのため、使用できません</Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: '13px' }}>撮影済みの画像から名刺を登録します</Text>
                )}
              </div>
            </Card>

            {/* 撮影しないで名刺を登録する（より弱い機能のため、控えめなテキストリンクに変更） */}
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Button
                type="link"
                size="small"
                icon={<UserAddOutlined />}
                onClick={async () => {
                  // 前回データをクリア
                  await indexedDBStorage.removeItem('scanResult');
                  await indexedDBStorage.removeItem('editResultBackup');
                  await indexedDBStorage.removeItem('backSideImage');
                  await indexedDBStorage.removeItem('currentFollowNo');

                  // 手動入力モード
                  await indexedDBStorage.setItem('isManualInput', 'true');
                  // 空のscanResultを作成して保存
                  const emptyScanResult = {
                      success: true,
                      data: {},
                      apiResponse: null
                  };
                  await indexedDBStorage.setItem('scanResult', JSON.stringify(emptyScanResult));
                  await indexedDBStorage.setItem('editResultBackup', JSON.stringify(emptyScanResult));

                  navigate(getRoutePath('/edit'));
                }}
                style={{ color: '#8c8c8c', fontSize: '13px' }}
              >
                撮影しないで名刺を登録する
              </Button>
            </div>
          </Col>
        </Row>
        )}
        </>
        ) : (!authorityResolved || !authorityKnown) ? (
        /*
          権限（authoritylist）取得が確定するまではバナーを出さずローディングを表示する。
          restoreSession は保存済みユーザーで先に描画し authoritylist は後追い取得のため、
          取得前の一瞬は「権限なし」に見える。リリースビルドは描画が速く、この一瞬に
          下の「再ログインが必要」バナーが誤表示される（デバッグ/ブラウザでは出ない）。
          取得完了（成功/失敗）後に初めて権限有無を判定する。
          さらに authorityResolved=true でも authoritylist が未取得(null=取得失敗)の層は、
          「真の無権限([]取得済)」と区別してバナーを出さずローディング表示にする
          （authorityKnown=Array.isArray でガード。誤検知バナーの居座り防止）。
        */
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <Spin />
        </div>
        ) : (
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <Text type="secondary" style={{ fontSize: 16 }}>
            {/*
              ▼ リリース初期用：既存ユーザーにはまだロール権限が付与されていないため、
                再ログインで最新の権限（authoritylist）を取り直してもらう案内を表示する。
                全ユーザーへの権限付与が完了したら、下の「本来の文言」に差し替えること。
            */}
            バージョンアップに伴い、再ログインが必要です。<br />
            お手数ですが、一度ログアウトのうえ、再度ログインしてください。

            {/*
              ▼ 本来の文言（権限付与完了後にこちらへ切り替える）：
              必要な権限がありません。<br />お手数ですが管理者にお問い合わせください。<br />
              （権限が付与されているのに表示されない場合は、一度ログアウトして再度ログインしてください。）
            */}
          </Text>
        </div>
        )}

      </Content>

      {!isFlutterApp && (
        <Footer style={{ textAlign: 'center' }}>
          UP-TONE ©{new Date().getFullYear()} Apto Inc. All Rights Reserved.
        </Footer>
      )}
    </Layout>
  );
};

export default MenuPage;
